// src/pages/Enseignants/ListingAdherents/ListingAdherentsPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import { FaExchangeAlt, FaEdit, FaPlus, FaTrashAlt } from "react-icons/fa";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import useScrollToTop from "../../../hooks/useScollToTop";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import { getDojos } from "../../../services/dojoServices";
import { getGroupsList } from "../../../services/groupServices";
import { getSettings } from "../../../services/settingsServices";
import {
  getAllAdherentsListWithGroup,
  getBirthdayAdherents,
  updateTeacherNotes,
  deleteAdherent,
  updateBlessures,
} from "../../../services/adherentServices";

import ModalFicheAdherent from "./ModalFicheAdherent";
import EditAdherentAdminModal from "./../../PagesAdministrateurs/ListingAdherentsAdmin/EditAdherentAdminModal";
import ManageGroupModal from "./ManageGroupModal";
import BirthdaySection from "./BirthdaySection";

import FiltersBar from "./FiltersBar";
import GroupHeader from "./GroupHeader";
import AdherentsGrid from "./AdherentsGrid";
import AdherentsTable from "./AdherentsTable";
import {
  getDefaultPhoto,
  seasonNeighbors,
  extractEmailsFromAdherent,
  statusColor,
  sorters,
} from "./utils";
import { IoMdContacts } from "react-icons/io";

import { reglementSorter } from "../../../components/Adherents/ReglementStatusBadge";

const ListingAdherentsPage = () => {
  useScrollToTop();

  // -------------------- User / rôle --------------------
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isAdmin = userRole === "admin";

  // -------------------- Data --------------------
  const [dojos, setDojos] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState("");

  const [allGroups, setAllGroups] = useState([]); // tous les groupes bruts
  const [groups, setGroups] = useState([]); // groupes filtrés par dojo
  const [filterGroup, setFilterGroup] = useState("");

  const [adherents, setAdherents] = useState([]);
  const [birthdayAdherents, setBirthdayAdherents] = useState([]);

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");

  // -------------------- UI / Filtres --------------------

  const [search, setSearch] = useState("");

  const [hideLicencieArret, setHideLicencieArret] = useState(true);

  const [statutInscription, setStatutInscription] = useState("");

  // Nouveau filtre règlement inscription
  const [reglementFilter, setReglementFilter] = useState("");

  // Tri
  const [sortBy, setSortBy] = useState("alpha");

  // Affichage cartes / tableau
  const [viewMode, setViewMode] = useState("grid");

  const [loading, setLoading] = useState(true);

  // -------------------- Modales --------------------
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isChangeGroupModalOpen, setIsChangeGroupModalOpen] = useState(false);
  const [isManageGroupModalOpen, setIsManageGroupModalOpen] = useState(false);
  const [selectedAdherent, setSelectedAdherent] = useState(null);

  // Notes / Blessures (admin)
  const [teacherNotes, setTeacherNotes] = useState("");
  const [isBlessed, setIsBlessed] = useState(false);
  const [dateReprise, setDateReprise] = useState("");

  // -------------------- Init --------------------
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [settings, dojoList, adherentList, birthdayList, groupList] =
          await Promise.all([
            getSettings(),
            getDojos(),
            getAllAdherentsListWithGroup(),
            getBirthdayAdherents(),
            getGroupsList(),
          ]);

        // Saison
        if (Array.isArray(settings) && settings[0]?.saison_active) {
          const { prev, next } = seasonNeighbors(settings[0].saison_active);
          setSeasons([prev, settings[0].saison_active, next]);
          setSelectedSeason(settings[0].saison_active);
        }

        // Dojos & groupes
        setDojos(dojoList || []);
        setAllGroups(groupList || []);
        if (dojoList?.length === 1) {
          const id = String(dojoList[0].id);
          setSelectedDojo(id);
          setGroups((groupList || []).filter((g) => String(g.dojo_id) === id));
        } else {
          setGroups(groupList || []);
        }

        setAdherents(Array.isArray(adherentList) ? adherentList : []);
        setBirthdayAdherents(Array.isArray(birthdayList) ? birthdayList : []);
      } catch (e) {
        console.error(e);
        toast.error("Erreur de chargement des données.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Si on change de dojo, on filtre la liste des groupes en conséquence
  const handleDojoChange = useCallback(
    async (val) => {
      setSelectedDojo(val);
      setGroups(
        val
          ? (allGroups || []).filter((g) => String(g.dojo_id) === String(val))
          : allGroups || [],
      );
      setFilterGroup("");
    },
    [allGroups],
  );

  // -------------------- Filtrage + Tri --------------------
  const birthdaySet = useMemo(
    () => new Set(birthdayAdherents.map((a) => a.id)),
    [birthdayAdherents],
  );

  const allowedGroupNames = useMemo(
    () => new Set((groups || []).map((g) => g.nom)),
    [groups],
  );

  const visibleAdherents = useMemo(() => {
    const q = search.trim().toLowerCase();

    // Dojo
    const inDojo = (a) =>
      !selectedDojo ||
      (a?.Groups || []).some((g) => allowedGroupNames.has(g.nom));

    // Groupe
    const inGroup = (a) =>
      !filterGroup || (a?.Groups || []).some((g) => g.nom === filterGroup);

    // Saison
    const inSeason = (a) =>
      !selectedSeason || a?.saison_active === selectedSeason;

    // Statut inscription
    const inStatus = (a) =>
      !statutInscription || a?.statut_inscription === statutInscription;

    // NOUVEAU : règlement inscription
    const inReglement = (a) =>
      !reglementFilter ||
      (a?.statut_reglement || "Inexistant") === reglementFilter;

    // Masquer licenciés en arrêt
    const notLicencieArret = (a) =>
      !hideLicencieArret || a?.statut_inscription !== "Licencié - Arrêt";

    // Recherche
    const matchSearch = (a) =>
      !q ||
      [
        a?.nom,
        a?.prenom,
        a?.licence_number,
        (a?.Groups || []).map((g) => g.nom).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);

    // FILTRAGE
    const list = (adherents || []).filter(
      (a) =>
        inDojo(a) &&
        inGroup(a) &&
        inSeason(a) &&
        inStatus(a) &&
        inReglement(a) &&
        notLicencieArret(a) &&
        matchSearch(a),
    );

    // TRI
    if (sortBy === "reglement") {
      list.sort(reglementSorter);
    } else if (sortBy === "alpha-desc") {
      list.sort((a, b) => -sorters.alpha(a, b));
    } else {
      const sorter = sorters[sortBy] || sorters.alpha;
      list.sort(sorter);
    }

    return list;
  }, [
    adherents,
    selectedDojo,
    filterGroup,
    selectedSeason,
    statutInscription,
    reglementFilter,
    hideLicencieArret,
    search,
    sortBy,
    allowedGroupNames,
  ]);

  // -------------------- Actions --------------------
  const openModal = (a) => {
    setSelectedAdherent(a);
    setTeacherNotes(a?.notes_enseignant || "");
    setIsBlessed(!!a?.is_blessed);
    setDateReprise(a?.date_reprise || "");
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAdherent(null);
  };

  const openEditModal = (a) => {
    setSelectedAdherent(a);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedAdherent(null);
  };

  const openChangeGroupModal = (a) => {
    setSelectedAdherent(a);
    setIsChangeGroupModalOpen(true);
  };
  const closeChangeGroupModal = (refresh = false) => {
    setIsChangeGroupModalOpen(false);
    setSelectedAdherent(null);
    if (refresh) reloadAdherents();
  };

  const openManageGroupModal = (a) => {
    setSelectedAdherent(a);
    setIsManageGroupModalOpen(true);
  };
  const closeManageGroupModal = (refresh = false) => {
    setIsManageGroupModalOpen(false);
    setSelectedAdherent(null);
    if (refresh) reloadAdherents();
  };

  const reloadAdherents = async () => {
    try {
      const list = await getAllAdherentsListWithGroup();
      setAdherents(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Impossible de recharger la liste.");
    }
  };

  const handleSaveTeacherNotes = async () => {
    if (!selectedAdherent) return;
    try {
      await updateTeacherNotes(selectedAdherent.id, {
        notes_enseignant: teacherNotes,
      });
      toast.success("Notes enseignant mises à jour.");
      await reloadAdherents();
      setIsModalOpen(false);
    } catch {
      toast.error("Erreur lors de la mise à jour des notes.");
    }
  };

  const handleSaveBlessed = async () => {
    if (!selectedAdherent) return;
    try {
      await updateBlessures(selectedAdherent.id, {
        is_blessed: isBlessed,
        date_reprise: dateReprise || null,
      });
      toast.success("Maladies/Blessures mises à jour.");
      await reloadAdherents();
      setIsModalOpen(false);
    } catch {
      toast.error("Erreur lors de la mise à jour des maladies/blessures.");
    }
  };

  const handleDeleteAdherent = async (id, nomComplet) => {
    if (
      !window.confirm(
        `Supprimer ${nomComplet} ? Cette action est irréversible.`,
      )
    )
      return;
    try {
      await deleteAdherent(id);
      toast.success("Adhérent supprimé.");
      closeModal();
      await reloadAdherents();
    } catch {
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleCopyEmails = async (list) => {
    const emails = new Set();
    list.forEach((a) =>
      extractEmailsFromAdherent(a).forEach((e) => emails.add(e)),
    );
    const txt = Array.from(emails).join(";");
    if (!txt) {
      toast.info("Aucun email à copier pour ce filtre.");
      return;
    }
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Emails copiés dans le presse-papiers.");
    } catch {
      toast.error("Impossible de copier les emails.");
    }
  };

  const handleExportCSV = (list) => {
    if (!list?.length) {
      toast.info("Aucune donnée à exporter.");
      return;
    }
    const rows = [
      [
        "Nom",
        "Prénom",
        "Statut inscription",
        "Règlement inscription",
        "Âge",
        "Groupes",
        "Email(s)",
      ].join(";"),
      ...list.map((a) => {
        const age = (() => {
          const d = a?.date_naissance ? new Date(a.date_naissance) : null;
          if (!d) return "";
          const now = new Date();
          let A = now.getFullYear() - d.getFullYear();
          const m = now.getMonth() - d.getMonth();
          if (m < 0 || (m === 0 && now.getDate() < d.getDate())) A--;
          return String(A);
        })();
        const groupsTxt = (a?.Groups || []).map((g) => g.nom).join(", ");
        const mails = extractEmailsFromAdherent(a).join(", ");
        return [
          a?.nom || "",
          a?.prenom || "",
          a?.statut_inscription || "",
          a?.statut_reglement || "Inexistant",
          age,
          groupsTxt,
          mails,
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(";");
      }),
    ].join("\n");

    const blob = new Blob([rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `adherents_${filterGroup || "tous"}_${
      selectedSeason || "saison"
    }.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // -------------------- Rendu --------------------
  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Enseignant"
          roleId="3"
          title="Listing des Adhérents"
          subtitle="Gérez les adhérents de vos groupes, consultez leurs fiches, ..."
          icon={IoMdContacts}
          align="left"
          className="mb-4"
        />

        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau dégradé (charte MonAppliClub) */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 sm:px-8 py-6 text-white">
            <h1 className="text-lg sm:text-xl font-semibold text-center">
              Listing des Adhérents par groupes
            </h1>
            <p className="text-sm/6 opacity-95 text-center mt-1">
              Filtrez par dojo / groupe / saison, basculez grille ↔ tableau,
              <br />
              Afficher la fiche adhérent (en cliquant sur la photo)
              <br />
              Editer un adhérent (passage de souris ou 3 points).
              <br />
              Répartir sur les groupes (passage de souris ou 3 points).
            </p>
          </div>

          {/* Anniversaires */}
          <div className="px-5 sm:px-8 pb-4">
            <BirthdaySection
              birthdayAdherents={birthdayAdherents}
              formatDate={() =>
                new Date().toLocaleDateString("fr-FR", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              }
              calculateAge={(date_naissance) => {
                const d = date_naissance ? new Date(date_naissance) : null;
                if (!d) return "";
                const now = new Date();
                let A = now.getFullYear() - d.getFullYear();
                const m = now.getMonth() - d.getMonth();
                if (m < 0 || (m === 0 && now.getDate() < d.getDate())) A--;
                return A;
              }}
              getDefaultPhoto={getDefaultPhoto}
              // token={user?.token || ""}
            />
          </div>

          {/* Filtres */}
          <div className="px-5 sm:px-8 pb-4">
            <FiltersBar
              loading={loading}
              // Dojos
              dojos={dojos}
              selectedDojo={selectedDojo}
              onDojoChange={handleDojoChange}
              // Groupes
              groups={groups}
              filterGroup={filterGroup}
              onGroupChange={setFilterGroup}
              // Saison
              seasons={seasons}
              selectedSeason={selectedSeason}
              onSeasonChange={setSelectedSeason}
              // Recherche
              search={search}
              onSearchChange={setSearch}
              // Statut inscription
              statutInscription={statutInscription}
              onStatutChange={setStatutInscription}
              // Règlement inscription
              reglementFilter={reglementFilter}
              onReglementChange={setReglementFilter}
              // Tri
              sortBy={sortBy}
              onSortByChange={setSortBy}
              // Affichage
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          </div>

          {/* En-tête groupe + actions */}
          <div className="px-5 sm:px-8">
            {filterGroup ? (
              <div className="px-5 sm:px-8">
                <GroupHeader
                  groupName={filterGroup || "Tous les groupes"}
                  count={visibleAdherents.length}
                  onCopyEmails={() => handleCopyEmails(visibleAdherents)}
                  onExportCSV={() => handleExportCSV(visibleAdherents)}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-slate-600 dark:text-slate-400">
                Sélectionnez un dojo puis un groupe pour afficher la liste.
              </div>
            )}
          </div>

          {/* Corps liste */}
          <div className="px-5 sm:px-8 pb-6">
            {viewMode === "grid" && (
              <AdherentsGrid
                adherents={visibleAdherents}
                birthdaySet={birthdaySet}
                getDefaultPhoto={getDefaultPhoto}
                onSelect={openModal}
                isAdmin={isAdmin}
                onEdit={openEditModal}
                onChangeGroup={openChangeGroupModal}
                onManageGroups={openManageGroupModal}
                refreshData={reloadAdherents}
              />
            )}

            {viewMode === "table" && (
              <AdherentsTable
                adherents={visibleAdherents}
                onRowClick={openModal} // Info (ouvre la fiche)
                onEdit={openEditModal} // ouvre modal d’édition
                onManageGroups={openManageGroupModal} // ouvre modal de gestion des groupes
                isAdmin={isAdmin}
                getDefaultPhoto={getDefaultPhoto}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modale fiche + panneaux admin */}
      {selectedAdherent && isModalOpen && (
        <ModalFicheAdherent onClose={closeModal}>
          {/* Blocs existants */}
          {/* Tu conserves tes composants FicheAdherent déjà câblés par adherentId */}
          {/* Si tu préfères tout charger par id côté enfant, garde comme avant */}
          {/* Ici : on passe l'id uniquement */}
          <div className="space-y-4">
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4">
                  <h3 className="font-semibold mb-2">Détails</h3>
                  {/* Composant existant */}
                  {/* @ts-ignore */}
                  <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg">
                    {/* Tu conserves tes composants existants */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ↓↓↓ Panneaux admin tels que dans ton code d'origine ↓↓↓ */}
          {isAdmin && (
            <>
              <div className="bg-gray-100 dark:bg-gray-500 px-6 pt-2 pb-4 shadow-lg max-w-[800px] mx-auto font-montserrat rounded-xl">
                <h3 className="text-lg font-bold mb-2">Notes Enseignants</h3>
                <textarea
                  value={teacherNotes}
                  onChange={(e) => setTeacherNotes(e.target.value)}
                  placeholder="Notes de l'enseignant"
                  className="textarea textarea-bordered w-full mb-4 dark:bg-slate-200"
                />
                <button
                  onClick={handleSaveTeacherNotes}
                  className="btn btn-primary"
                >
                  Sauvegarder les notes
                </button>

                <h3 className="text-lg font-bold mb-2 mt-4">
                  Maladies/Blessures
                </h3>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    checked={isBlessed}
                    onChange={(e) => setIsBlessed(e.target.checked)}
                    className="checkbox checkbox-primary"
                  />
                  <label className="text-sm">
                    Judoka(te) malade ou blessé(e)
                  </label>
                </div>
                {isBlessed && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">
                      Date de reprise
                    </label>
                    <input
                      type="date"
                      value={dateReprise || ""}
                      onChange={(e) => setDateReprise(e.target.value)}
                      className="input input-bordered w-full dark:bg-slate-200"
                    />
                  </div>
                )}
                <button onClick={handleSaveBlessed} className="btn btn-primary">
                  Sauvegarder maladies/blessures
                </button>
              </div>

              <div className="flex flex-wrap gap-3 justify-between mt-3">
                <button
                  onClick={() => openChangeGroupModal(selectedAdherent)}
                  className="btn btn-secondary gap-2"
                >
                  <FaExchangeAlt /> Changer de Groupe
                </button>
                <button
                  onClick={() => openManageGroupModal(selectedAdherent)}
                  className="btn btn-primary gap-2"
                >
                  <FaPlus /> Gérer les Groupes
                </button>
                <button
                  onClick={() => openEditModal(selectedAdherent)}
                  className="btn btn-info gap-2"
                >
                  <FaEdit /> Éditer Adhérent
                </button>
                <button
                  onClick={() =>
                    handleDeleteAdherent(
                      selectedAdherent.id,
                      `${selectedAdherent.nom} ${selectedAdherent.prenom}`,
                    )
                  }
                  className="btn btn-error gap-2"
                >
                  <FaTrashAlt /> Supprimer l’adhérent
                </button>
              </div>
            </>
          )}
        </ModalFicheAdherent>
      )}

      {/* Modales annexes */}
      {isEditModalOpen && selectedAdherent && (
        <EditAdherentAdminModal
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          adherent={selectedAdherent}
          refreshList={reloadAdherents}
        />
      )}

      {isManageGroupModalOpen && selectedAdherent && (
        <ManageGroupModal
          isOpen={isManageGroupModalOpen}
          onClose={closeManageGroupModal}
          adherentId={selectedAdherent.id}
          groups={allGroups}
          adherentGroups={selectedAdherent?.Groups || []}
          refreshData={reloadAdherents}
        />
      )}

      {selectedAdherent && isModalOpen && (
        <ModalFicheAdherent
          adherent={selectedAdherent}
          onClose={closeModal}
          isAdmin={isAdmin}
          onEdit={openEditModal} // tu as déjà cette callback
          onManageGroups={openManageGroupModal}
        />
      )}

      <ToastContainer />
    </section>
  );
};

export default ListingAdherentsPage;
